import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { acctSettings, eclMatrix, saveAcctSettings, saveEclMatrix } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { NeuButton, NeuCard, NeuInput } from "../components/neu/Neu";

const BUCKETS = ["0-30", "31-60", "61-90", "91-180", "180+"];

export default function AcctSettingsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [matrix, setMatrix] = useState(null);

  useEffect(() => {
    acctSettings().then(setSettings);
    eclMatrix().then(setMatrix);
  }, []);

  const saveThresholds = async () => {
    setSettings(await saveAcctSettings({ receipt_thresholds: settings.receipt_thresholds, receipt_required: settings.receipt_required }));
    toast.success(t("acct.settingsSaved"));
  };

  const saveMatrix = async () => {
    setMatrix(await saveEclMatrix(matrix));
    toast.success(t("acct.settingsSaved"));
  };

  if (!settings || !matrix) return <AcctShell titleKey="settings" />;

  return (
    <AcctShell titleKey="settings">
      <NeuCard className="grid gap-4 p-5" data-testid="receipt-settings">
        <div>
          <p className="text-sm font-bold">{t("acct.receiptCeiling")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("acct.receiptCeilingHint")}</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {Object.entries(settings.receipt_thresholds).map(([currency, value]) => (
            <div key={currency}>
              <p className="label-xs mb-2" dir="ltr">{currency}</p>
              <NeuInput
                data-testid={`threshold-${currency}`}
                type="number"
                value={value}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    receipt_thresholds: { ...prev.receipt_thresholds, [currency]: e.target.value },
                  }))
                }
              />
            </div>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[hsl(var(--primary))]"
            data-testid="receipt-required-toggle"
            checked={settings.receipt_required}
            onChange={(e) => setSettings((prev) => ({ ...prev, receipt_required: e.target.checked }))}
          />
          {t("acct.receiptRequiredToggle")}
        </label>
        <NeuButton variant="primary" className="w-fit" data-testid="threshold-save" onClick={saveThresholds}>
          {t("app.save")}
        </NeuButton>
      </NeuCard>

      <NeuCard className="grid gap-4 p-5" data-testid="ecl-settings">
        <p className="text-sm font-bold">{t("acct.eclTitle")}</p>
        <div className="grid gap-3 sm:grid-cols-5">
          {BUCKETS.map((bucket) => (
            <div key={bucket}>
              <p className="label-xs mb-2">{t(`acct.report.${bucket}`)}</p>
              <NeuInput
                data-testid={`ecl-${bucket}`}
                type="number"
                value={matrix[bucket]}
                onChange={(e) => setMatrix((prev) => ({ ...prev, [bucket]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <NeuButton variant="primary" className="w-fit" data-testid="ecl-save" onClick={saveMatrix}>
          {t("app.save")}
        </NeuButton>
      </NeuCard>
    </AcctShell>
  );
}
